import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import PersonIcon from '@mui/icons-material/Person';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GridOnIcon from '@mui/icons-material/GridOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SettingsIcon from '@mui/icons-material/Settings';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'; // Import $ icon
import ForkRightIcon from '@mui/icons-material/ForkRight'; // Import fork icon
import TuneIcon from '@mui/icons-material/Tune'; // Import tune icon for Advanced Settings
import appLogo from '../assets/images/FERS-Navigator-App-Header.png';
import AuthDisplay from './AuthDisplay'; // Import the AuthDisplay component
import SaveStatusIndicator from './SaveStatusIndicator';
import { useUser } from '../context/UserContext';
import useUserData from '../hooks/useUserData';

const drawerWidth = 240;

const navItems = [
  { text: 'Biographic Data', icon: <PersonIcon /> },
  { text: 'Scenarios', icon: <ForkRightIcon /> },
  { text: 'Other Income', icon: <AttachMoneyIcon /> },
  { text: 'Projections', icon: <AssessmentIcon /> },
  { text: 'Tables', icon: <GridOnIcon /> },
  { text: 'Advanced Settings', icon: <TuneIcon /> },
];

export default function MainLayout({ children, selectedIndex, setSelectedIndex }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // md breakpoint can be adjusted  
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Get user data and save status for the save indicator
  const { user } = useUser();
  const { isLoading, lastSaved, error, saveData } = useUserData();
  

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  // Only show the currently active section's content
  const renderSection = () => {
    if (Array.isArray(children)) {
      return children[selectedIndex] || null;
    }
    return children;
  };


  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <AppBar
        position="fixed"
        sx={{
          width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
          ml: isMobile ? 0 : `${drawerWidth}px`,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: '#1a2e46',
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between', // Changed to space-between to put auth on the right
            minHeight: isMobile ? 64 : 70,
            px: isMobile ? 1 : 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ textAlign: 'left' }}>
              <img
                src={appLogo}
                alt="FERS Navigator Logo"
                style={{
                  maxWidth: 380,
                  width: 'auto',
                  maxHeight: isMobile ? 81 : 156,
                  height: 'auto',
                  display: 'block',
                  margin: 0,
                }}
              />
            </Box>
          </Box>
          
          {/* Auth Display and Save Status in the toolbar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SaveStatusIndicator 
              isLoading={isLoading}
              lastSaved={lastSaved}
              error={error}
              isGuest={!user || user.isGuest}
              onManualSave={saveData}
            />
            <AuthDisplay />
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={isMobile ? handleDrawerToggle : undefined}
        ModalProps={isMobile ? { keepMounted: true } : {}}
        // Temporary drawer should not have fixed width in sx affecting AppBar
        // It should be controlled by its paper's sx
        sx={{
          display: { xs: isMobile ? 'block' : 'none', md: 'block' }, // Show on md and up, or if mobile and open
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: '#23395d',
            color: '#fff',
          },
        }}
      >
        {/* Spacer to match logo/header height, use Box to ensure Drawer respects the height */}
        <Box sx={{ height: 140, display: 'block' }} />
        <Box sx={{ overflow: 'auto' }}>
          <List sx={{ flexGrow: 1 }}>
            {navItems.map((item, index) => (
              <ListItem
                key={item.text}
                selected={selectedIndex === index}
                onClick={() => {
                  setSelectedIndex(index);
                  if (isMobile) {
                    handleDrawerToggle(); // Close drawer on item click on mobile
                  }
                }}
                sx={{ borderRadius: 2, mb: 0.5, backgroundColor: selectedIndex === index ? '#34517a' : 'inherit' }}
              >
                <ListItemIcon sx={{ color: '#fff' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />

        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
        flexGrow: 1,
        p: 2,
        // marginLeft: isMobile ? 0 : `${drawerWidth}px`, // This was causing double margin with permanent drawer
        backgroundColor: '#f8fafc',
        maxWidth: '900px',
        marginRight: 'auto',
        marginLeft: isMobile ? 'auto' : 0, // Corrected for desktop: main content starts after drawer
        width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)` // Ensure content takes up available space
      }}
      >
        <Toolbar />
        {renderSection()}
      </Box>
    </Box>
  );
}
